import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Table, ProgressBar, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { 
  Building, 
  Users, 
  TrendingUp, 
  DollarSign, 
  Plus, 
  Settings, 
  BarChart3,
  AlertTriangle,
  Calendar,
  Briefcase,
  MapPin,
  Activity,
  User,
  Mail,
  Phone
} from 'lucide-react';
import CreateBoutique from '../boutiques/CreateBoutique';
import { boutiqueService } from '../../services/boutiqueService';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  // États pour la gestion du modal de création
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedBoutique, setSelectedBoutique] = useState(null);
  
  // ✅ NOUVEAU : États pour les vraies données
  const [globalStats, setGlobalStats] = useState({
    totalBoutiques: 0,
    activeBoutiques: 0,
    totalUsers: 0,
    totalRevenue: 0,
    monthlyGrowth: 0
  });

  const [topBoutiques, setTopBoutiques] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [systemAlerts, setSystemAlerts] = useState([]);

  // ✅ NOUVEAU : Charger les données réelles au montage
  useEffect(() => {
    loadDashboardData();
  }, []);

  // ✅ NOUVEAU : Fonction pour charger toutes les données du dashboard
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Charger les boutiques
      const boutiquesResponse = await boutiqueService.getAllBoutiques();
      
      if (boutiquesResponse.success) {
        const boutiquesData = boutiquesResponse.data || [];
        
        // Calculer les statistiques globales
        const stats = calculateGlobalStats(boutiquesData);
        setGlobalStats(stats);
        
        // Prendre les top 5 boutiques (par revenus ou nombre d'utilisateurs)
        const sortedBoutiques = boutiquesData
          .sort((a, b) => (b.total_users || 0) - (a.total_users || 0))
          .slice(0, 5);
        setTopBoutiques(sortedBoutiques);
        
        // Générer des activités récentes basées sur les données
        generateRecentActivities(boutiquesData);
        
        // Générer des alertes système
        generateSystemAlerts(boutiquesData);
        
      } else {
        console.error('Erreur chargement dashboard:', boutiquesResponse.message);
      }
    } catch (error) {
      console.error('Erreur dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ NOUVEAU : Calculer les statistiques globales
  const calculateGlobalStats = (boutiques) => {
    const totalBoutiques = boutiques.length;
    const activeBoutiques = boutiques.filter(b => 
      b.status === 'ACTIVE' || b.is_active === true
    ).length;
    
    const totalUsers = boutiques.reduce((sum, b) => sum + (b.total_users || 0), 0);
    const totalRevenue = boutiques.reduce((sum, b) => sum + (b.total_revenue || 0), 0);
    
    // Simuler une croissance (en réalité, il faudrait comparer avec le mois précédent)
    const monthlyGrowth = totalBoutiques > 0 ? 
      Math.round((activeBoutiques / totalBoutiques) * 10 * 100) / 100 : 0;

    return {
      totalBoutiques,
      activeBoutiques,
      totalUsers,
      totalRevenue,
      monthlyGrowth
    };
  };

  // ✅ NOUVEAU : Générer des activités récentes
  const generateRecentActivities = (boutiques) => {
    const activities = [];
    
    // Activité pour chaque boutique récente
    boutiques.slice(0, 3).forEach(boutique => {
      activities.push({
        type: 'boutique_created',
        message: `Boutique "${boutique.name}" créée`,
        user: 'Super Admin',
        timestamp: boutique.created_at || boutique.createdAt,
        icon: Building,
        color: 'success'
      });
      
      if (boutique.manager_name || boutique.managerName) {
        activities.push({
          type: 'manager_assigned',
          message: `Gestionnaire "${boutique.manager_name || boutique.managerName}" assigné`,
          user: 'Super Admin',
          timestamp: boutique.created_at || boutique.createdAt,
          icon: Users,
          color: 'info'
        });
      }
    });
    
    setRecentActivities(activities.slice(0, 5));
  };

  // ✅ NOUVEAU : Générer des alertes système
  const generateSystemAlerts = (boutiques) => {
    const alerts = [];
    
    // Alertes pour boutiques sans gestionnaire
    const boutiquesWithoutManager = boutiques.filter(b => 
      !b.manager_name && !b.managerName
    );
    
    if (boutiquesWithoutManager.length > 0) {
      alerts.push({
        type: 'warning',
        title: 'Boutiques sans gestionnaire',
        message: `${boutiquesWithoutManager.length} boutique(s) n'ont pas de gestionnaire assigné`,
        icon: AlertTriangle
      });
    }
    
    // Alerte pour boutiques inactives
    const inactiveBoutiques = boutiques.filter(b => 
      b.status !== 'ACTIVE' && b.is_active !== true
    );
    
    if (inactiveBoutiques.length > 0) {
      alerts.push({
        type: 'info',
        title: 'Boutiques inactives',
        message: `${inactiveBoutiques.length} boutique(s) sont inactives`,
        icon: Activity
      });
    }
    
    setSystemAlerts(alerts);
  };

  // Fonctions utilitaires
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'ACTIVE': { variant: 'success', label: 'Active' },
      'ARCHIVED': { variant: 'secondary', label: 'Archivée' },
      'SUSPENDED': { variant: 'warning', label: 'Suspendue' },
      'CONFIGURATION': { variant: 'info', label: 'Configuration' }
    };

    const config = statusConfig[status] || statusConfig['CONFIGURATION'];
    
    return (
      <Badge bg={config.variant}>
        {config.label}
      </Badge>
    );
  };

  // ✅ NOUVEAU : Fonction pour obtenir le nom du gestionnaire
  const getManagerInfo = (boutique) => {
    const managerName = boutique.manager_name || boutique.managerName || 'Non assigné';
    const managerEmail = boutique.manager_email || boutique.managerEmail;
    const managerPhone = boutique.manager_phone || boutique.managerPhone;
    const isActive = boutique.manager_is_active !== false && boutique.managerIsActive !== false;
    
    return { managerName, managerEmail, managerPhone, isActive };
  };

  // Gestionnaire de création de boutique
  const handleCreateBoutique = () => {
    setSelectedBoutique(null);
    setShowCreateModal(true);
  };

  // ✅ CORRIGÉ : Fonction pour sauvegarder la boutique et recharger les données
  const handleSaveBoutique = async (data) => {
    setShowCreateModal(false);
    
    // Recharger les données du dashboard pour afficher la nouvelle boutique
    await loadDashboardData();
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement du dashboard...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* En-tête du dashboard */}
      <Row className="mb-4">
        <Col>
          <div className="dashboard-header">
            <h1 className="dashboard-title">
              👑 Dashboard Super Administrateur
            </h1>
            <p className="dashboard-subtitle">
              Vue d'ensemble de toutes les boutiques et utilisateurs du système
            </p>
          </div>
        </Col>
      </Row>

      {/* Statistiques globales */}
      <Row className="mb-4">
        <Col lg={3} md={6} className="mb-3">
          <Card className="stat-card border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="stat-icon bg-primary">
                  <Building size={24} className="text-white" />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{globalStats.totalBoutiques}</div>
                  <div className="stat-title">Total Boutiques</div>
                  <div className="stat-subtitle">{globalStats.activeBoutiques} actives</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="stat-card border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="stat-icon bg-success">
                  <Users size={24} className="text-white" />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{globalStats.totalUsers}</div>
                  <div className="stat-title">Total Utilisateurs</div>
                  <div className="stat-subtitle">Tous rôles confondus</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="stat-card border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="stat-icon bg-warning">
                  <DollarSign size={24} className="text-white" />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{formatCurrency(globalStats.totalRevenue)}</div>
                  <div className="stat-title">Revenus Globaux</div>
                  <div className="stat-subtitle">Ce mois</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="stat-card border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="stat-icon bg-info">
                  <TrendingUp size={24} className="text-white" />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{globalStats.monthlyGrowth}%</div>
                  <div className="stat-title">Croissance</div>
                  <div className="stat-subtitle">Ce mois</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Actions rapides */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header>
              <h4 className="mb-0">🚀 Actions Rapides</h4>
            </Card.Header>
            <Card.Body>
              <div className="d-flex flex-wrap gap-3">
                <Button 
                  variant="outline-primary" 
                  className="quick-action-btn"
                  onClick={() => navigate('/admin/boutiques')}
                >
                  <Building size={20} />
                  <span>Gérer Boutiques</span>
                </Button>
                <Button 
                  variant="outline-info" 
                  className="quick-action-btn"
                  onClick={() => navigate('/admin/users')}
                >
                  <Users size={20} />
                  <span>Gérer Utilisateurs</span>
                </Button>
                <Button 
                  variant="outline-success" 
                  className="quick-action-btn"
                  onClick={handleCreateBoutique}
                >
                  <Plus size={20} />
                  <span>Créer Boutique</span>
                </Button>
                <Button 
                  variant="outline-warning" 
                  className="quick-action-btn"
                  onClick={() => navigate('/admin/settings')}
                >
                  <Settings size={20} />
                  <span>Paramètres</span>
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Contenu principal */}
      <Row className="mb-4">
        {/* ✅ CORRIGÉ : Top Boutiques avec vraies données */}
        <Col lg={8} className="mb-4">
          <Card className="border-0 shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">🏆 Boutiques Récentes</h4>
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={() => navigate('/admin/boutiques')}
              >
                Voir tout
              </Button>
            </Card.Header>
            <Card.Body>
              {topBoutiques.length > 0 ? (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Boutique</th>
                      <th>Gestionnaire</th>
                      <th>Statut</th>
                      <th>Utilisateurs</th>
                      <th>Créée le</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topBoutiques.map(boutique => {
                      const managerInfo = getManagerInfo(boutique);
                      
                      return (
                        <tr key={boutique.id}>
                          <td>
                            <div>
                              <strong>{boutique.name}</strong>
                              {boutique.description && (
                                <div className="small text-muted">{boutique.description}</div>
                              )}
                            </div>
                          </td>
                          
                          {/* ✅ CORRIGÉ : Colonne Gestionnaire */}
                          <td>
                            <div className="manager-info">
                              <div className="d-flex align-items-center">
                                <User size={14} className="me-2 text-muted" />
                                <span>{managerInfo.managerName}</span>
                                {!managerInfo.isActive && (
                                  <Badge bg="warning" size="sm" className="ms-2">Inactif</Badge>
                                )}
                              </div>
                              {managerInfo.managerEmail && (
                                <div className="small text-muted d-flex align-items-center mt-1">
                                  <Mail size={12} className="me-1" />
                                  {managerInfo.managerEmail}
                                </div>
                              )}
                              {managerInfo.managerPhone && (
                                <div className="small text-muted d-flex align-items-center">
                                  <Phone size={12} className="me-1" />
                                  {managerInfo.managerPhone}
                                </div>
                              )}
                            </div>
                          </td>
                          
                          <td>{getStatusBadge(boutique.status)}</td>
                          
                          <td>
                            <Badge bg="info">{boutique.total_users || 0}</Badge>
                          </td>
                          
                          <td>
                            <span className="small text-muted">
                              {formatDate(boutique.created_at || boutique.createdAt)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center text-muted py-4">
                  <Building size={48} className="mb-3 opacity-50" />
                  <p>Aucune boutique créée pour le moment</p>
                  <Button variant="success" onClick={handleCreateBoutique}>
                    <Plus size={16} className="me-2" />
                    Créer la première boutique
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Alertes système */}
        <Col lg={4} className="mb-4">
          <Card className="border-0 shadow-sm">
            <Card.Header>
              <h4 className="mb-0">⚠️ Alertes Système</h4>
            </Card.Header>
            <Card.Body>
              {systemAlerts.length > 0 ? (
                <div className="space-y-3">
                  {systemAlerts.map((alert, index) => (
                    <div key={index} className={`alert alert-${alert.type} d-flex align-items-start`}>
                      <alert.icon size={20} className="me-2 mt-1 flex-shrink-0" />
                      <div>
                        <div className="fw-bold">{alert.title}</div>
                        <div className="small">{alert.message}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  <Activity size={48} className="mb-3 opacity-50" />
                  <p>Aucune alerte système</p>
                  <small>Tout fonctionne parfaitement !</small>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Activités récentes */}
      <Row>
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header>
              <h4 className="mb-0">📋 Activités Récentes</h4>
            </Card.Header>
            <Card.Body>
              {recentActivities.length > 0 ? (
                <div className="activity-feed">
                  {recentActivities.map((activity, index) => (
                    <div key={index} className="activity-item d-flex align-items-start mb-3">
                      <div className={`activity-icon bg-${activity.color} me-3`}>
                        <activity.icon size={16} className="text-white" />
                      </div>
                      <div className="flex-grow-1">
                        <div className="activity-message">{activity.message}</div>
                        <div className="activity-meta small text-muted">
                          Par {activity.user} • {formatDate(activity.timestamp)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  <Calendar size={48} className="mb-3 opacity-50" />
                  <p>Aucune activité récente</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal de création de boutique */}
      <Modal 
        show={showCreateModal} 
        onHide={() => setShowCreateModal(false)}
        size="lg"
        backdrop="static"
      >
        <CreateBoutique 
          boutique={selectedBoutique}
          onClose={() => setShowCreateModal(false)}
          onSave={handleSaveBoutique}
        />
      </Modal>

      <style jsx>{`
        .dashboard-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .dashboard-title {
          color: #1e293b;
          font-weight: 700;
          font-size: 2.5rem;
          margin-bottom: 0.5rem;
        }

        .dashboard-subtitle {
          color: #64748b;
          font-size: 1.1rem;
        }

        .stat-card {
          transition: transform 0.2s;
        }

        .stat-card:hover {
          transform: translateY(-2px);
        }

        .stat-icon {
          width: 60px;
          height: 60px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1rem;
        }

        .stat-content {
          text-align: left;
        }

        .stat-value {
          font-size: 1.8rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }

        .stat-title {
          color: #64748b;
          font-weight: 600;
          margin-bottom: 0.25rem;
          font-size: 0.9rem;
        }

        .stat-subtitle {
          color: #94a3b8;
          font-size: 0.8rem;
        }

        .quick-action-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 500;
          transition: all 0.2s;
        }

        .quick-action-btn:hover {
          transform: translateY(-1px);
        }

        .manager-info {
          font-size: 0.9rem;
        }

        .activity-icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .activity-message {
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.25rem;
        }

        .activity-meta {
          color: #6b7280;
        }
      `}</style>
    </Container>
  );
};

export default SuperAdminDashboard;